import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Save, Trash2, AlertCircle, Eye } from "lucide-react";
import {
  adminCreateArticle,
  adminDeleteArticle,
  adminListArticles,
  adminUpdateArticle,
  adminListTools,
} from "@/admin/cmsApi";
import {
  BilingualField,
  FieldError,
  JsonEditor,
  Label,
  SelectInput,
  SlugChipPicker,
  TextInput,
} from "@/admin/cmsForms";

const EMPTY = {
  slug: "",
  category: "",
  date: "",
  coverVariant: "default",
  coverUrl: "",
  dateLabel: { ar: "", en: "" },
  readingTime: { ar: "", en: "" },
  relatedTools: [],
  title: { ar: "", en: "" },
  excerpt: { ar: "", en: "" },
  summary: { ar: "", en: "" },
  metaTitle: { ar: "", en: "" },
  metaDescription: { ar: "", en: "" },
  keywords: { ar: [], en: [] },
  body: [],
};

const COVER_OPTIONS = [
  { value: "default", label: "Default" },
  { value: "presentations", label: "Presentations" },
  { value: "research", label: "Research" },
];

const BODY_HELP = `Body is a JSON array of blocks. Supported shapes:
[
  { "type": "heading", "level": 2, "text": { "ar": "…", "en": "…" } },
  { "type": "paragraph", "text": { "ar": "…", "en": "…" } },
  { "type": "takeaway", "text": { "ar": "…", "en": "…" } },
  { "type": "link", "url": "https://…", "label": { "ar": "…", "en": "…" } }
]`;

/**
 * Convert the comma-separated keyword UI value into the `{ar, en}` list shape
 * the backend expects, and back. We keep the textarea UX but round-trip cleanly.
 */
const keywordsToText = (kw) => ({
  ar: (kw?.ar || []).join(", "),
  en: (kw?.en || []).join(", "),
});
const textToKeywords = (text) => ({
  ar: (text?.ar || "").split(",").map((s) => s.trim()).filter(Boolean),
  en: (text?.en || "").split(",").map((s) => s.trim()).filter(Boolean),
});

export default function AdminArticleEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id || id === "new";

  const [form, setForm] = useState(EMPTY);
  const [keywordsText, setKeywordsText] = useState({ ar: "", en: "" });
  const [bodyText, setBodyText] = useState("[]");
  const [bodyError, setBodyError] = useState("");
  const [categories, setCategories] = useState([]);
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [savedAt, setSavedAt] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [currentId, setCurrentId] = useState(isNew ? null : id);

  const load = useCallback(async () => {
    setErrorMsg("");
    try {
      const [adata, tdata] = await Promise.all([adminListArticles(), adminListTools()]);
      setCategories(adata.categories || []);
      setTools(tdata.items || []);
      if (!isNew) {
        const match = (adata.items || []).find((a) => a.id === id);
        if (!match) {
          setErrorMsg("Article not found.");
        } else {
          setForm({
            slug: match.slug || "",
            category: match.category || "",
            date: match.date || "",
            coverVariant: match.coverVariant || "default",
            coverUrl: match.coverUrl || "",
            dateLabel: match.dateLabel || { ar: "", en: "" },
            readingTime: match.readingTime || { ar: "", en: "" },
            relatedTools: match.relatedTools || [],
            title: match.title || { ar: "", en: "" },
            excerpt: match.excerpt || { ar: "", en: "" },
            summary: match.summary || { ar: "", en: "" },
            metaTitle: match.metaTitle || { ar: "", en: "" },
            metaDescription: match.metaDescription || { ar: "", en: "" },
            keywords: match.keywords || { ar: [], en: [] },
            body: match.body || [],
          });
          setKeywordsText(keywordsToText(match.keywords));
          setBodyText(JSON.stringify(match.body || [], null, 2));
          setCurrentId(match.id);
        }
      } else {
        setBodyText("[]");
      }
    } catch (e) {
      setErrorMsg(e.message || "Failed to load article.");
    } finally {
      setLoading(false);
    }
  }, [id, isNew]);

  useEffect(() => { load(); }, [load]);

  const setField = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setFieldErrors((e) => ({ ...e, [k]: undefined }));
  };
  const setBilingual = (k, side, v) => {
    setForm((f) => ({ ...f, [k]: { ...(f[k] || {}), [side]: v } }));
    setFieldErrors((e) => ({ ...e, [`${k}.${side}`]: undefined }));
  };

  const onSave = async (e) => {
    e.preventDefault();
    setErrorMsg("");
    setFieldErrors({});
    setSavedAt(null);

    // Parse body JSON
    let parsedBody;
    try {
      parsedBody = JSON.parse(bodyText || "[]");
      if (!Array.isArray(parsedBody)) throw new Error("Body must be a JSON array.");
      setBodyError("");
    } catch (err) {
      setBodyError(`Invalid JSON: ${err.message}`);
      return;
    }

    setSaving(true);
    try {
      const payload = {
        slug: form.slug.trim(),
        category: form.category,
        date: form.date,
        coverVariant: form.coverVariant,
        coverUrl: (form.coverUrl || "").trim(),
        dateLabel: form.dateLabel,
        readingTime: form.readingTime,
        relatedTools: form.relatedTools,
        title: form.title,
        excerpt: form.excerpt,
        summary: form.summary,
        metaTitle: form.metaTitle,
        metaDescription: form.metaDescription,
        keywords: textToKeywords(keywordsText),
        body: parsedBody,
      };
      if (isNew || !currentId) {
        const created = await adminCreateArticle(payload);
        navigate(`/admin/articles/${created.id}`, { replace: true });
      } else {
        await adminUpdateArticle(currentId, payload);
        setSavedAt(new Date());
      }
    } catch (err) {
      if (err.status === 422 && err.fieldErrors) {
        setFieldErrors(err.fieldErrors);
      } else if (err.status === 409) {
        setFieldErrors({ slug: "Slug already in use. Pick another." });
      } else {
        setErrorMsg(err.message || "Save failed.");
      }
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async () => {
    if (!currentId) return;
    setSaving(true);
    try {
      await adminDeleteArticle(currentId);
      navigate("/admin/articles", { replace: true });
    } catch (err) {
      setErrorMsg(err.message || "Delete failed.");
      setConfirmDelete(false);
    } finally {
      setSaving(false);
    }
  };

  const toolOptions = useMemo(
    () => tools.map((t) => ({ value: t.slug, label: `${t.name} (${t.slug})` })),
    [tools]
  );
  const categoryOptions = useMemo(
    () => categories.map((c) => ({ value: c.id, label: c.label?.en || c.id })),
    [categories]
  );

  if (loading) {
    return (
      <div data-testid="admin-article-editor-loading" className="space-y-3">
        <div className="h-6 w-32 bg-[color:var(--sas-accent-soft)] rounded animate-pulse" />
        <div className="h-10 w-1/2 bg-[color:var(--sas-accent-soft)] rounded animate-pulse" />
        <div className="mt-6 h-96 bg-white border border-[color:var(--sas-line)] rounded-xl animate-pulse" />
      </div>
    );
  }

  const publicHref = !isNew && form.slug ? `/ar/articles/${form.slug}` : null;

  return (
    <div data-testid="admin-article-editor">
      <Link
        to="/admin/articles"
        className="inline-flex items-center gap-1.5 text-xs text-[color:var(--sas-ink-2)] hover:text-[color:var(--sas-ink)] mb-6"
        data-testid="admin-article-back"
      >
        <ArrowLeft size={14} /> All articles
      </Link>
      <div className="sas-eyebrow mb-2">Content · Article</div>
      <h1 className="sas-h1">
        {isNew ? "New article" : (form.title?.en || form.title?.ar || "Edit article")}
      </h1>

      {errorMsg && (
        <div className="mt-4 flex items-start gap-2 rounded-lg border border-[color:#fecaca] bg-[#fef2f2] px-3 py-2.5 text-[13px] text-[#991b1b]" role="alert" data-testid="admin-article-editor-error">
          <AlertCircle size={14} className="mt-0.5 shrink-0" aria-hidden />
          <span>{errorMsg}</span>
        </div>
      )}
      {savedAt && !errorMsg && (
        <div className="mt-4 rounded-lg border border-[color:#bbf7d0] bg-[#f0fdf4] px-3 py-2.5 text-[13px] text-[#166534]" role="status" data-testid="admin-article-saved">
          Saved. Public /articles pages reflect the latest version immediately on refresh.
        </div>
      )}

      <form onSubmit={onSave} className="mt-6 flex flex-col gap-6" noValidate>
        <section className="rounded-xl border border-[color:var(--sas-line)] bg-white p-5 md:p-6 flex flex-col gap-5">
          <h2 className="text-[13px] font-semibold text-[color:var(--sas-ink)] uppercase tracking-wide">
            Identity
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="article-slug" required hint="URL-safe, unique">Slug</Label>
              <TextInput id="article-slug" value={form.slug} onChange={(v) => setField("slug", v.toLowerCase())} placeholder="what-is-ai-for-students" dir="ltr" testid="admin-article-slug" error={fieldErrors.slug} />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="article-category" required>Category</Label>
              <SelectInput id="article-category" value={form.category} onChange={(v) => setField("category", v)} options={categoryOptions} testid="admin-article-category" error={fieldErrors.category} />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="article-date">Publish date (YYYY-MM-DD)</Label>
              <TextInput id="article-date" value={form.date} onChange={(v) => setField("date", v)} placeholder="2026-01-15" dir="ltr" testid="admin-article-date" />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="article-cover">Cover variant</Label>
              <SelectInput id="article-cover" value={form.coverVariant} onChange={(v) => setField("coverVariant", v)} options={COVER_OPTIONS} testid="admin-article-cover" />
            </div>
            <div className="md:col-span-3 flex flex-col gap-1.5">
              <Label
                htmlFor="article-cover-url"
                hint="Optional. Direct URL to a hero photo. When set it overrides the gradient cover variant on the article card and detail page."
              >
                Cover image URL (optional)
              </Label>
              <TextInput
                id="article-cover-url"
                value={form.coverUrl || ""}
                onChange={(v) => setField("coverUrl", v)}
                placeholder="https://images.unsplash.com/…"
                dir="ltr"
                testid="admin-article-cover-url"
                error={fieldErrors.coverUrl}
              />
            </div>
          </div>
          <BilingualField
            name="dateLabel"
            label="Date label (as shown on the page)"
            valueAr={form.dateLabel.ar}
            valueEn={form.dateLabel.en}
            onChangeAr={(v) => setBilingual("dateLabel", "ar", v)}
            onChangeEn={(v) => setBilingual("dateLabel", "en", v)}
            errors={fieldErrors}
          />
          <BilingualField
            name="readingTime"
            label="Reading time"
            valueAr={form.readingTime.ar}
            valueEn={form.readingTime.en}
            onChangeAr={(v) => setBilingual("readingTime", "ar", v)}
            onChangeEn={(v) => setBilingual("readingTime", "en", v)}
            errors={fieldErrors}
          />
        </section>

        <section className="rounded-xl border border-[color:var(--sas-line)] bg-white p-5 md:p-6 flex flex-col gap-5">
          <h2 className="text-[13px] font-semibold text-[color:var(--sas-ink)] uppercase tracking-wide">
            Content
          </h2>
          <BilingualField name="title" label="Title" required valueAr={form.title.ar} valueEn={form.title.en} onChangeAr={(v) => setBilingual("title", "ar", v)} onChangeEn={(v) => setBilingual("title", "en", v)} errors={fieldErrors} />
          <BilingualField name="excerpt" label="Excerpt (card / listing preview)" valueAr={form.excerpt.ar} valueEn={form.excerpt.en} onChangeAr={(v) => setBilingual("excerpt", "ar", v)} onChangeEn={(v) => setBilingual("excerpt", "en", v)} type="textarea" rows={3} errors={fieldErrors} />
          <BilingualField name="summary" label="Summary (shown above body)" valueAr={form.summary.ar} valueEn={form.summary.en} onChangeAr={(v) => setBilingual("summary", "ar", v)} onChangeEn={(v) => setBilingual("summary", "en", v)} type="textarea" rows={3} errors={fieldErrors} />
        </section>

        <section className="rounded-xl border border-[color:var(--sas-line)] bg-white p-5 md:p-6 flex flex-col gap-5">
          <h2 className="text-[13px] font-semibold text-[color:var(--sas-ink)] uppercase tracking-wide">
            Body
          </h2>
          <div className="flex flex-col gap-2">
            <Label hint="JSON array of blocks">Body blocks</Label>
            <JsonEditor
              id="article-body"
              value={bodyText}
              onChange={setBodyText}
              rows={16}
              testid="admin-article-body"
              error={bodyError}
              placeholder={BODY_HELP}
            />
            <p className="text-[11.5px] text-[color:var(--sas-ink-2)] whitespace-pre-line leading-relaxed">{BODY_HELP}</p>
          </div>
        </section>

        <section className="rounded-xl border border-[color:var(--sas-line)] bg-white p-5 md:p-6 flex flex-col gap-5">
          <h2 className="text-[13px] font-semibold text-[color:var(--sas-ink)] uppercase tracking-wide">
            SEO
          </h2>
          <BilingualField name="metaTitle" label="Meta title" valueAr={form.metaTitle.ar} valueEn={form.metaTitle.en} onChangeAr={(v) => setBilingual("metaTitle", "ar", v)} onChangeEn={(v) => setBilingual("metaTitle", "en", v)} errors={fieldErrors} />
          <BilingualField name="metaDescription" label="Meta description" valueAr={form.metaDescription.ar} valueEn={form.metaDescription.en} onChangeAr={(v) => setBilingual("metaDescription", "ar", v)} onChangeEn={(v) => setBilingual("metaDescription", "en", v)} type="textarea" rows={3} errors={fieldErrors} />
          <BilingualField name="keywords" label="Keywords (comma-separated)" valueAr={keywordsText.ar} valueEn={keywordsText.en} onChangeAr={(v) => setKeywordsText((k) => ({ ...k, ar: v }))} onChangeEn={(v) => setKeywordsText((k) => ({ ...k, en: v }))} type="textarea" rows={2} errors={fieldErrors} />
        </section>

        <section className="rounded-xl border border-[color:var(--sas-line)] bg-white p-5 md:p-6 flex flex-col gap-4">
          <h2 className="text-[13px] font-semibold text-[color:var(--sas-ink)] uppercase tracking-wide">
            Related tools
          </h2>
          <p className="text-[12.5px] text-[color:var(--sas-ink-2)]">
            Surface these tools at the bottom of the article. Tap a chip to toggle.
          </p>
          <SlugChipPicker
            allOptions={toolOptions}
            value={form.relatedTools}
            onChange={(v) => setField("relatedTools", v)}
            testid="admin-article-related-tools"
          />
          <FieldError error={fieldErrors.relatedTools} id="admin-article-related-tools-error" />
        </section>

        <div className="flex flex-wrap items-center gap-3">
          <button type="submit" disabled={saving} className="sas-btn sas-btn-primary" data-testid="admin-article-save">
            <Save size={15} aria-hidden /> {saving ? "Saving…" : (isNew ? "Create article" : "Save changes")}
          </button>
          {publicHref && (
            <a href={publicHref} target="_blank" rel="noreferrer" className="sas-btn sas-btn-ghost" data-testid="admin-article-preview">
              <Eye size={15} aria-hidden /> Preview (Arabic)
            </a>
          )}
          {!isNew && (
            confirmDelete ? (
              <div className="flex items-center gap-2 text-[12.5px] text-[#991b1b]">
                <span>Delete this article?</span>
                <button type="button" onClick={onDelete} disabled={saving} className="sas-btn sas-btn-primary" style={{ background: "#b91c1c" }} data-testid="admin-article-delete-confirm">
                  <Trash2 size={15} aria-hidden /> Yes, delete
                </button>
                <button type="button" onClick={() => setConfirmDelete(false)} className="sas-btn sas-btn-ghost" data-testid="admin-article-delete-cancel">
                  Cancel
                </button>
              </div>
            ) : (
              <button type="button" onClick={() => setConfirmDelete(true)} className="sas-btn sas-btn-ghost" data-testid="admin-article-delete">
                <Trash2 size={15} aria-hidden /> Delete…
              </button>
            )
          )}
        </div>
      </form>
    </div>
  );
}
